import React, { MouseEvent, ReactElement, useCallback, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { compose } from 'redux';
import { isEmpty } from 'b2b-apps/shop/common/utils/empty/empty';
import { config } from 'b2b-apps/shop/config';
import { ENTRY_ID_TYPE, useEntry } from 'b2b-apps/shop/content/hooks/useEntry';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { useRouterPush } from 'b2b-apps/shop/routes/hooks';
import { openLink } from 'b2b-apps/shop/routes/utils/url';
import {
  EnhancedComponentProps,
  EnhancedComponentType,
  InjectedProps,
  LogoSliderData,
  LogoSliderItem,
  WrappedComponentType,
} from './';

export const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const content = useEntry(ENTRY_ID_TYPE.footer);
    const footerLogos = useEntry(ENTRY_ID_TYPE.logoSlider);
    const push = useRouterPush();
    const data = useMemo(
      (): Record<string, any> => {
        if (!content.isLoaded || !content.data || isEmpty(content.data)) {
          return {};
        }

        return content.data;
      },
      [content],
    );

    const logoSliderData = useMemo(
      (): LogoSliderData => {
        if (!footerLogos.isLoaded || !footerLogos.data || isEmpty(footerLogos.data)) {
          return {
            title: '',
            items: [],
          };
        }

        return {
          title: footerLogos?.data?.module__logo_slider?.title || '',
          items: footerLogos?.data?.module__logo_slider?.group?.reduce?.(
            (
              result: LogoSliderItem[],
              item: Record<string, any>,
            ): LogoSliderItem[] => {
              if (item.logo_image) {
                result.push({
                  url: item.url,
                  brandName: item.brand_name,
                  src: item.logo_image.url,
                });
              }

              return result;
            },
            [],
          ) || [],
        };
      },
      [
        footerLogos,
      ],
    );

    const handleLinkClick = useCallback(
      (url: string): OnAnchorClickType => (
        e: MouseEvent<HTMLAnchorElement>,
      ): void => {
        e.preventDefault();
        openLink(url, push, e);
      },
      [push],
    );

    const t = useTranslate();
    const text = {
      copyright:
        '\xA9 '
        + t('{year} {shopName} - All rights reserved.', {
          year: new Date().getFullYear(),
          shopName: config.shopName,
        }),
      newsletterTitle: t('Email Newsletter!'),
      newsletterText: t(
        'Subscribe now and receive weekly emails with exciting offers, latest news and updates.',
      ),
      email: (email: string) => t(
        'E-mail {email}',
        { email: email },
        'Email address in footer',
      ),
      phone: (phone: string) => t(
        'Phone {number}',
        { number: phone },
        'Phone number in footer',
      ),
    };

    const injectedProps: InjectedProps = {
      data,
      logoSliderData,
      onLinkClick: handleLinkClick,
      text,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return compose<EnhancedComponentType>(
    withRouter,
  )(React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps)));
};
